import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { BarChart3, UtensilsCrossed, TrendingUp, TrendingDown, Scale, Flame, Beef, ChevronLeft, ChevronRight } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { toast } from 'sonner';

const ACTIVITY_LABELS = { sedentaire: 'Sedentaire', leger: 'Leger', modere: 'Modere', actif: 'Actif', tres_actif: 'Tres actif' };
const GOAL_LABELS = { perte: 'Perte', perte_gras: 'Perte de gras', seche: 'Seche', maintien: 'Maintien', prise: 'Prise' };
const CHART_TICK = { fill: '#AAAAAA', fontSize: 10 };
const CHART_TOOLTIP = { backgroundColor: '#1B1B1B', border: '1px solid #2A2A2E', borderRadius: '6px', color: '#FFFFFF' };

export function MemberHistoryDialog({ memberId, memberName, open, onClose }) {
  const [tab, setTab] = useState('metrics');
  const [metrics, setMetrics] = useState([]);
  const [meals, setMeals] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    try {
      const [mRes, nRes, sRes] = await Promise.all([
        apiClient.get(`/admin/members/${memberId}/metrics-history?limit=50`),
        apiClient.get(`/admin/members/${memberId}/meals-history?days=30`),
        apiClient.get(`/admin/members/${memberId}/nutrition-summary`),
      ]);
      setMetrics(mRes.data);
      setMeals(nRes.data);
      setSummary(sRes.data);
    } catch { toast.error('Erreur chargement historique'); }
    finally { setLoading(false); }
  }, [memberId]);

  useEffect(() => { if (open && memberId) fetchData(); }, [open, memberId, fetchData]);

  const weightData = [...metrics].reverse().map(m => ({
    date: m.timestamp ? new Date(m.timestamp).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '?',
    poids: m.weight, gras: m.body_fat, muscle: m.muscle_mass,
  }));

  const dailyData = meals?.daily_summary ? Object.entries(meals.daily_summary)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, d]) => ({
      date: day.slice(5),
      cal: Math.round(d.calories),
      prot: Math.round(d.protein),
    })) : [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#1B1B1B] border-[#2A2A2E] text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#FF6B35]" /> Historique — {memberName}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            {/* Summary cards */}
            {summary && (
              <div className="grid grid-cols-4 gap-2">
                <SummaryChip label="Poids" value={summary.latest_weight ? `${summary.latest_weight}kg` : '—'} color="#FF6B35" />
                <SummaryChip label="TDEE" value={summary.latest_tdee ? `${summary.latest_tdee}` : '—'} color="#FFB800" />
                <SummaryChip label="Repas" value={summary.total_meals} color="#3b82f6" />
                <SummaryChip label="Moy/j" value={`${summary.avg_daily_calories_7d}`} color="#2e7d32" />
              </div>
            )}

            {summary && (
              <div className="flex flex-wrap gap-1.5">
                {summary.gender && <span className="text-[9px] bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded">{summary.gender === 'male' ? 'Homme' : 'Femme'}</span>}
                {summary.height && <span className="text-[9px] bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded">{summary.height}cm</span>}
                {summary.activity_level && <span className="text-[9px] bg-[#FF6B35]/15 text-[#FF6B35] px-2 py-0.5 rounded">{ACTIVITY_LABELS[summary.activity_level]}</span>}
                {summary.nutrition_goal && <span className="text-[9px] bg-[#FFB800]/15 text-[#FFB800] px-2 py-0.5 rounded">{GOAL_LABELS[summary.nutrition_goal]}</span>}
                {summary.onboarding_complete && <span className="text-[9px] bg-[#2e7d32]/15 text-[#2e7d32] px-2 py-0.5 rounded">Onboarding OK</span>}
              </div>
            )}

            {/* Tabs */}
            <div className="flex border-b border-[#2A2A2E]">
              {[{ id: 'metrics', label: 'Metriques', icon: Scale }, { id: 'nutrition', label: 'Nutrition', icon: UtensilsCrossed }].map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold ${tab === t.id ? 'text-[#FF6B35] border-b-2 border-[#FF6B35]' : 'text-zinc-500'}`}>
                  <t.icon className="w-3 h-3" /> {t.label}
                </button>
              ))}
            </div>

            {tab === 'metrics' && (
              <div className="space-y-4">
                {weightData.length > 1 ? (
                  <>
                    <div className="h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={weightData}>
                          <XAxis dataKey="date" tick={CHART_TICK} axisLine={false} tickLine={false} />
                          <YAxis tick={CHART_TICK} axisLine={false} tickLine={false} width={30} domain={['dataMin - 1', 'dataMax + 1']} />
                          <Tooltip contentStyle={CHART_TOOLTIP} />
                          <Line type="monotone" dataKey="poids" stroke="#FF6B35" strokeWidth={2} dot={{ fill: '#FF6B35', r: 2 }} name="Poids (kg)" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="h-32">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={weightData}>
                          <XAxis dataKey="date" tick={CHART_TICK} axisLine={false} tickLine={false} />
                          <YAxis tick={CHART_TICK} axisLine={false} tickLine={false} width={30} />
                          <Tooltip contentStyle={CHART_TOOLTIP} />
                          <Line type="monotone" dataKey="gras" stroke="#FF00B8" strokeWidth={2} dot={{ r: 2 }} name="Gras (%)" />
                          <Line type="monotone" dataKey="muscle" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2 }} name="Muscle (kg)" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-zinc-500 text-center py-4">Pas assez de donnees pour le graphique</p>
                )}

                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {metrics.slice(0, 20).map(m => (
                    <div key={m.id} className="flex items-center gap-2 text-[10px] px-2 py-1.5 bg-[#0F0F0F] rounded">
                      <span className="text-zinc-500 font-mono w-16">{m.timestamp?.slice(0, 10)}</span>
                      <span className="text-white">{m.weight}kg</span>
                      {m.body_fat && <span className="text-[#FF00B8]">{m.body_fat}% MG</span>}
                      {m.muscle_mass && <span className="text-[#3b82f6]">{m.muscle_mass}kg muscle</span>}
                      {m.tdee && <span className="text-zinc-400">TDEE:{m.tdee}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === 'nutrition' && (
              <div className="space-y-4">
                {dailyData.length > 1 ? (
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dailyData}>
                        <XAxis dataKey="date" tick={CHART_TICK} axisLine={false} tickLine={false} />
                        <YAxis tick={CHART_TICK} axisLine={false} tickLine={false} width={35} />
                        <Tooltip contentStyle={CHART_TOOLTIP} />
                        <Bar dataKey="cal" fill="#FF6B35" radius={[2, 2, 0, 0]} name="Calories" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-sm text-zinc-500 text-center py-4">Pas de repas enregistres ces 30 derniers jours</p>
                )}

                {meals?.meals?.length > 0 && (
                  <div className="space-y-1 max-h-[200px] overflow-y-auto">
                    {meals.meals.slice(0, 30).map(m => (
                      <div key={m.id} className="flex items-center gap-2 text-[10px] px-2 py-1.5 bg-[#0F0F0F] rounded">
                        <span className="text-zinc-500 font-mono w-16">{m.timestamp?.slice(0, 10)}</span>
                        <span className="text-[#FF6B35] font-bold">{Math.round(m.calories)}cal</span>
                        <span className="text-[#FF00B8]">{Math.round(m.protein)}P</span>
                        <span className="text-[#FFB800]">{Math.round(m.carbs)}G</span>
                        <span className="text-[#3b82f6]">{Math.round(m.fat)}L</span>
                        <span className="text-zinc-500 truncate flex-1">{m.description?.slice(0, 30)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SummaryChip({ label, value, color }) {
  return (
    <div className="bg-[#0F0F0F] rounded-lg p-2 text-center" style={{ borderTop: `2px solid ${color}` }}>
      <p className="font-mono text-sm font-bold text-white">{value}</p>
      <p className="text-[8px] text-zinc-400">{label}</p>
    </div>
  );
}
